import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import MobileSelect from "@/components/drill/MobileSelect";
import { useAutoSave } from "@/hooks/use-auto-save";

const STATUS_OPTIONS = ["Active", "Completed", "On Hold"].map((s) => ({ value: s, label: s }));
const HOLE_TYPE_OPTIONS = ["Diamond", "Conventional", "Chipping", "Air Core", "RC"].map((s) => ({ value: s, label: s }));

export default function HoleForm({ initialData, onSubmit, onAutoSave, submitting, submitLabel, holeType: defaultHoleType, defaultRigId, lockRigId }) {
  const [name, setName] = useState(initialData?.name ?? "");
  const [rigId, setRigId] = useState(initialData?.rig_id ?? defaultRigId ?? "");
  const [description, setDescription] = useState(initialData?.description ?? "");
  const [barrelLength, setBarrelLength] = useState(
    initialData?.barrel_length != null ? String(initialData.barrel_length) : ""
  );
  const [tableHeight, setTableHeight] = useState(
    initialData?.table_height != null ? String(initialData.table_height) : ""
  );
  const [chipBhaLength, setChipBhaLength] = useState(
    initialData?.bha_length != null ? String(initialData.bha_length) : ""
  );
  const [chipTableHeight, setChipTableHeight] = useState(
    initialData?.chip_table_height != null ? String(initialData.chip_table_height) : ""
  );
  const [coreTableHeight, setCoreTableHeight] = useState(
    initialData?.core_table_height != null ? String(initialData.core_table_height) :
    (initialData?.table_height != null ? String(initialData.table_height) : "")
  );
  const [runSize, setRunSize] = useState(
    initialData?.run_size != null ? String(initialData.run_size) : ""
  );
  const [bhaLength, setBhaLength] = useState(
    initialData?.bha_length != null ? String(initialData.bha_length) : ""
  );
  const [collars, setCollars] = useState(
    Array.isArray(initialData?.collars) ? initialData.collars.map(String) : []
  );
  const [status, setStatus] = useState(initialData?.status ?? "Active");
  const [holeType, setHoleType] = useState(initialData?.hole_type ?? defaultHoleType ?? "Diamond");
  const [drillerInput, setDrillerInput] = useState("");
  const [drillers, setDrillers] = useState(
    Array.isArray(initialData?.drillers) ? initialData.drillers : []
  );

  const addDriller = () => {
    const trimmed = drillerInput.trim();
    if (!trimmed) return;
    setDrillers((prev) => [...prev, trimmed]);
    setDrillerInput("");
  };

  const barrelTableHeight = useMemo(
    () =>
      barrelLength !== "" && tableHeight !== ""
        ? +(+barrelLength - +tableHeight).toFixed(2)
        : "",
    [barrelLength, tableHeight]
  );

  const conventionalBarrelTableHeight = useMemo(
    () =>
      barrelLength !== "" && coreTableHeight !== ""
        ? +(+barrelLength - +coreTableHeight).toFixed(2)
        : "",
    [barrelLength, coreTableHeight]
  );

  const conventionalBhaTableHeight = useMemo(
    () =>
      chipBhaLength !== "" && chipTableHeight !== ""
        ? +(+chipBhaLength - +chipTableHeight).toFixed(2)
        : "",
    [chipBhaLength, chipTableHeight]
  );

  const buildPayload = () => ({
    name: name.trim(),
    hole_type: holeType,
    ...(holeType === "Conventional" ? { conventional_method: initialData?.conventional_method === "Chip" ? "Chip" : "Core" } : {}),
    rig_id: rigId.trim() || undefined,
    drillers,
    description: description.trim(),
    barrel_length: barrelLength === "" ? undefined : +barrelLength,
    table_height: holeType === "Conventional"
      ? (coreTableHeight === "" ? undefined : +coreTableHeight)
      : (tableHeight === "" ? undefined : +tableHeight),
    barrel_table_height: holeType === "Conventional"
      ? (conventionalBarrelTableHeight === "" ? undefined : +conventionalBarrelTableHeight)
      : (barrelTableHeight === "" ? undefined : +barrelTableHeight),
    run_size: runSize === "" ? undefined : +runSize,
    ...(holeType === "Conventional" ? {
      core_table_height: coreTableHeight === "" ? undefined : +coreTableHeight,
      chip_table_height: chipTableHeight === "" ? undefined : +chipTableHeight,
      bha_length: chipBhaLength === "" ? undefined : +chipBhaLength,
      bha_table_height: conventionalBhaTableHeight === "" ? undefined : +conventionalBhaTableHeight,
    } : (initialData ? { bha_length: bhaLength === "" ? undefined : +bhaLength } : {})),
    collars: collars
      .map((c) => (c === "" ? undefined : +c))
      .filter((c) => c !== undefined && !isNaN(c)),
    status,
  });

  // Auto-save hole edits every 1 minute
  useAutoSave(!!onAutoSave, () => onAutoSave?.(buildPayload()));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit(buildPayload());
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Hole ID *</Label>
          <Input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. DDH-001"
            className="rounded-xl"
          />
        </div>
        <div>
          <Label>Hole Type</Label>
          <MobileSelect
            value={holeType}
            onValueChange={setHoleType}
            options={HOLE_TYPE_OPTIONS}
            placeholder="Select type"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Rig ID</Label>
          <Input
            value={rigId}
            onChange={(e) => setRigId(e.target.value)}
            placeholder="e.g. RIG-04"
            className="rounded-xl"
            readOnly={lockRigId}
          />
        </div>
        <div className="col-span-2">
          <Label>Driller</Label>
          <div className="flex gap-2">
            <Input
              value={drillerInput}
              onChange={(e) => setDrillerInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDriller(); } }}
              placeholder="Add driller"
              dir="ltr"
              autoComplete="off"
              spellCheck={false}
              style={{ direction: "ltr", unicodeBidi: "plaintext", textAlign: "left", writingMode: "horizontal-tb" }}
              className="rounded-xl flex-1 min-w-0 h-12 text-base"
            />
            <Button
              type="button"
              variant="outline"
              onClick={addDriller}
              className="rounded-xl gap-2 shrink-0"
            >
              <Plus className="w-4 h-4" /> Add Driller
            </Button>
          </div>
        </div>
      </div>
      {drillers.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {drillers.map((d, i) => (
            <span key={i} dir="ltr" style={{ direction: "ltr", unicodeBidi: "plaintext" }} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-sm text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              {d}
              <button type="button" onClick={() => setDrillers((prev) => prev.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div>
        <Label>Description</Label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Location, project, notes…"
          className="rounded-xl min-h-[80px]"
        />
      </div>
      {holeType === "Conventional" ? (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="grid grid-cols-2 bg-slate-50 dark:bg-slate-900/70">
            <div className="px-4 py-3 text-center font-semibold border-r border-slate-200 dark:border-slate-800">Chip</div>
            <div className="px-4 py-3 text-center font-semibold">Core</div>
          </div>
          <div className="grid grid-cols-2 divide-x divide-slate-200 dark:divide-slate-800">
            <div className="p-4 space-y-3">
              <div>
                <Label>BHA (m)</Label>
                <Input type="number" step="0.01" value={chipBhaLength} onChange={(e) => setChipBhaLength(e.target.value)} className="rounded-xl" />
              </div>
              <div>
                <Label>Table Height (m)</Label>
                <Input type="number" step="0.01" value={chipTableHeight} onChange={(e) => setChipTableHeight(e.target.value)} className="rounded-xl" />
              </div>
              <div>
                <Label>BHA-Table Height (m)</Label>
                <Input readOnly value={conventionalBhaTableHeight === "" ? "" : conventionalBhaTableHeight} className="rounded-xl bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400" />
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <Label>Barrel (m)</Label>
                <Input type="number" step="0.01" value={barrelLength} onChange={(e) => setBarrelLength(e.target.value)} className="rounded-xl" />
              </div>
              <div>
                <Label>Table Height (m)</Label>
                <Input type="number" step="0.01" value={coreTableHeight} onChange={(e) => setCoreTableHeight(e.target.value)} className="rounded-xl" />
              </div>
              <div>
                <Label>Barrel-Table Height (m)</Label>
                <Input readOnly value={conventionalBarrelTableHeight === "" ? "" : conventionalBarrelTableHeight} className="rounded-xl bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400" />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            {holeType !== "Chipping" && (
              <div>
                <Label>Barrel Length (m)</Label>
                <Input type="number" step="0.01" value={barrelLength} onChange={(e) => setBarrelLength(e.target.value)} className="rounded-xl" />
              </div>
            )}
            <div>
              <Label>Table Height (m)</Label>
              <Input type="number" step="0.01" value={tableHeight} onChange={(e) => setTableHeight(e.target.value)} className="rounded-xl" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Rod Length (m)</Label>
              <Input type="number" step="0.01" value={runSize} onChange={(e) => setRunSize(e.target.value)} className="rounded-xl" />
            </div>
            {initialData && <div>
              <Label>BHA Length (m)</Label>
              <Input type="number" step="0.01" value={bhaLength} onChange={(e) => setBhaLength(e.target.value)} className="rounded-xl" />
            </div>}
          </div>
        </>
      )}
      {holeType === "Conventional" && (
        <div>
          <Label>Rod Length (m)</Label>
          <Input type="number" step="0.01" value={runSize} onChange={(e) => setRunSize(e.target.value)} className="rounded-xl" />
        </div>
      )}
      {/* Collar cells — only shown when at least one collar has been added */}
      {collars.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          {collars.map((c, i) => (
            <div key={i}>
              <Label>Collar {i + 1} (m)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.01"
                  value={c}
                  onChange={(e) =>
                    setCollars((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))
                  }
                  className="rounded-xl"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 shrink-0 text-red-500 hover:text-red-600"
                  onClick={() => setCollars((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        onClick={() => setCollars((prev) => [...prev, ""])}
        className="rounded-xl gap-2"
      >
        <Plus className="w-4 h-4" /> Add collar
      </Button>
      {holeType !== "Chipping" && holeType !== "Conventional" && (
        <div>
          <Label>Barrel-Table Height (m)</Label>
          <Input
            readOnly
            value={barrelTableHeight === "" ? "" : barrelTableHeight}
            className="rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-400"
          />
          <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Auto: barrel − table</p>
        </div>
      )}
      <div>
        <Label>Status</Label>
        <MobileSelect
          value={status}
          onValueChange={setStatus}
          options={STATUS_OPTIONS}
          placeholder="Select status"
        />
      </div>
      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={submitting}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl gap-2"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {submitLabel ?? (initialData ? "Save" : "Create & Open")}
        </Button>
      </div>
    </form>
  );
}